import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { SortableHeader, useSort } from '../ui/SortableHeader';
import { usePersistedState } from '../ui/usePersistedState';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Plus, Search, Trash2, Eye, X, ScanLine, ClipboardCheck, Truck, FileSpreadsheet, FileText, CheckSquare, Square, ShieldCheck, QrCode, Database, UserCheck, Images, Upload, Camera, Package } from 'lucide-react';
import FloatingActions from '../FloatingActions';
import { CopyIconButton, CopyAllButton } from '../shared/CopyableLabel';
import { exportView, exportFullDump } from '../../lib/exportXlsx';
import MatrixAssignDialog from './MatrixAssignDialog';
import AssetForm from './AssetForm';
import AssetDetail from './AssetDetail';
import BarcodeScanner from './BarcodeScanner';
import NameplateScanDialog from './NameplateScanDialog';
import BulkImportDialog from './BulkImportDialog';
import AssignmentCell from './AssignmentCell';
import LocationTransferDialog from './LocationTransferDialog';
import LazyAuthImage from '../shared/LazyAuthImage';
import { QrLabelDialog } from '../QrLabelDialog';
import { ownershipOptions, ownershipShort, ownershipTone } from './ownership';
import { useTranslation } from 'react-i18next';
import useDeepShortcut from '../../hooks/useDeepShortcut';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssetsList = ({ categories, conditions, suppliers, employees, projects, isAdmin, onDataChange, config = {}, navigateTo, bookingDelivery = null, onBookingDeliveryDone }) => {
  const { t, i18n } = useTranslation();
  const { storageLocations = [] } = config;
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = usePersistedState('assets.search', '');
  const [filterCategory, setFilterCategory] = usePersistedState('assets.filterCategory', '');
  const [filterType, setFilterType] = usePersistedState('assets.filterType', '');
  const [filterCondition, setFilterCondition] = usePersistedState('assets.filterCondition', '');
  const [filterLocation, setFilterLocation] = usePersistedState('assets.filterLocation', '');
  const [filterOwnership, setFilterOwnership] = usePersistedState('assets.filterOwnership', '');
  const [createOpen, setCreateOpen] = useState(false);
  const [plateScanOpen, setPlateScanOpen] = useState(false);
  const [scanPrefill, setScanPrefill] = useState(null);
  const [editAsset, setEditAsset] = useState(null);
  const [detailAsset, setDetailAsset] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [qrAsset, setQrAsset] = useState(null);
  const [bulkQrOpen, setBulkQrOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [sortKey, sortDir, toggleSort, comparator, resetSort] = useSort('inventory_number', 'asc', 'inventory-assets');

  const isViewCustomized = sortKey !== 'inventory_number'
    || sortDir !== 'asc'
    || search !== ''
    || filterCategory !== ''
    || filterType !== ''
    || filterCondition !== ''
    || filterLocation !== ''
    || filterOwnership !== '';
  const [batchAction, setBatchAction] = useState(null); // 'delete' | 'update'
  const [batchCondition, setBatchCondition] = useState('');
  const [batchCategory, setBatchCategory] = useState('');

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCategory) params.category_id = filterCategory;
      if (filterType) params.asset_type = filterType;
      if (filterCondition) params.condition = filterCondition;
      if (filterLocation && filterLocation !== 'unassigned') params.storage_location_id = filterLocation;
      if (filterOwnership) params.ownership = filterOwnership;
      const r = await axios.get(`${API}/inventory/assets`, { params });
      setAssets(r.data);
    } catch { toast.error(t('inv.assets.loadFailed')); }
    setLoading(false);
  }, [search, filterCategory, filterType, filterCondition, filterOwnership, filterLocation]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  // Dashboard shortcuts: pin the open tool (or the New asset form) and
  // reopen it straight from a tile.
  useDeepShortcut({
    active: !!detailAsset,
    tab: 'inventory',
    context: { invSubTab: 'assets', focusAssetId: detailAsset?.id },
    label: detailAsset?.inventory_number || detailAsset?.name,
    subtitle: detailAsset?.name || '',
    icon: 'assets',
    perms: ['inventory.view'],
    priority: 3,
    focusKey: 'focusAssetId',
    onFocus: async (id) => {
      const found = assets.find((a) => a.id === id);
      if (found) { setDetailAsset(found); return; }
      try {
        const r = await axios.get(`${API}/inventory/assets/${id}`);
        setDetailAsset(r.data);
      } catch { toast.error(t('inv.assets.loadFailed')); }
    },
  });
  useDeepShortcut({
    active: createOpen,
    tab: 'inventory',
    context: { invSubTab: 'assets', create: 'asset' },
    label: t('shortcuts.qc.asset'),
    icon: 'assets',
    perms: ['inventory.edit'],
    priority: 4,
    focusKey: 'create',
    onFocus: (v) => { if (v === 'asset') setCreateOpen(true); },
  });

  // Arrived here from "Scan tools into this delivery": go straight to the
  // camera. Every asset saved while this is set joins the batch.
  useEffect(() => {
    if (bookingDelivery) {
      setScanPrefill(null);
      setPlateScanOpen(true);
    }
  }, [bookingDelivery]);

  // 'unassigned' is not a real id, so it is filtered here rather than server-side.
  const locationScoped = filterLocation === 'unassigned'
    ? assets.filter((a) => !a.storage_location_id)
    : assets;

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/inventory/assets/${deleteId}`);
      toast.success(t('inv.assets.deleted'));
      setDeleteId(null);
      fetchAssets();
      onDataChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || t('inv.common.deleteFailed')); }
  };

  const handleSaved = (opts) => {
    setCreateOpen(false);
    setEditAsset(null);
    setScanPrefill(null);
    fetchAssets();
    onDataChange?.();
    // "Save & scan next": straight back to the camera for the next tool.
    if (opts?.scanNext) setPlateScanOpen(true);
  };

  /** Hand the driver a tick sheet for whatever location is on screen. */
  const printStocktake = async () => {
    try {
      const r = await axios.get(
        `${API}/inventory/storage-locations/${filterLocation}/stocktake.pdf`,
        { params: { lang: i18n.language === 'pl' ? 'pl' : 'en' }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(`stocktake-${filterLocation}.pdf`);
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t('inv.assets.stocktakeFailed')); }
  };

  const clearFilters = () => {
    setSearch('');
    setFilterCategory('');
    setFilterType('');
    setFilterCondition('');
    setFilterOwnership('');
    setFilterLocation('');
    resetSort();
  };

  const hasFilters = isViewCustomized;

  const toggleSelect = (id) => setSelected((prev) => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });
  const toggleAll = () => setSelected(selected.size === locationScoped.length ? new Set() : new Set(locationScoped.map(a => a.id)));

  const handleBatchDelete = async () => {
    try {
      const r = await axios.post(`${API}/inventory/assets/batch/delete`, [...selected]);
      toast.success(t('inv.assets.batchDeleted', { count: r.data.deleted_count }));
      setSelected(new Set());
      setBatchAction(null);
      fetchAssets();
      onDataChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || t('inv.assets.batchDeleteFailed')); }
  };

  const handleBatchUpdate = async () => {
    const params = new URLSearchParams();
    [...selected].forEach(id => params.append('asset_ids', id));
    if (batchCondition) params.append('condition', batchCondition);
    if (batchCategory) params.append('category_id', batchCategory);
    try {
      const r = await axios.post(`${API}/inventory/assets/batch/update?${params.toString()}`);
      toast.success(t('inv.assets.batchUpdated', { count: r.data.updated_count }));
      setSelected(new Set());
      setBatchAction(null);
      setBatchCondition('');
      setBatchCategory('');
      fetchAssets();
      onDataChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || t('inv.assets.batchUpdateFailed')); }
  };

  const handleExport = async (format) => {
    try {
      const params = {};
      if (filterCategory) params.category_id = filterCategory;
      if (filterType) params.asset_type = filterType;
      if (filterCondition) params.condition = filterCondition;
      if (filterOwnership) params.ownership = filterOwnership;
      const r = await axios.get(`${API}/inventory/assets/export/${format}`, {
        params, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(`assets_export.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('inv.common.formatExported', { format: format.toUpperCase() }));
    } catch { toast.error(t('inv.common.exportFailed')); }
  };

  // The illustrated dossier: details, ownership, who has it, inspections and
  // photos. Ticked rows win; otherwise the current filters decide.
  const handleAssetReport = async () => {
    const ids = Array.from(selected);
    toast.info(ids.length ? t('inv.assets.buildingReportCount', { count: ids.length }) : t('inv.assets.buildingReportView'));
    try {
      const r = await axios.post(`${API}/inventory/assets/report/pdf`, {
        asset_ids: ids,
        include_photos: true,
        ...(ids.length ? {} : {
          category_id: filterCategory || null,
          asset_type: filterType || null,
          condition: filterCondition || null,
          ownership: filterOwnership || null,
        }),
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(`asset_report_${new Date().toISOString().slice(0, 10)}.pdf`);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('inv.assets.reportDownloaded'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('inv.assets.reportFailed'));
    }
  };

  const handleComplianceReport = async () => {
    try {
      const r = await axios.get(`${API}/inventory/compliance-report`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(`compliance_report_${new Date().toISOString().slice(0,10)}.xlsx`);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('inv.assets.complianceDownloaded'));
    } catch { toast.error(t('inv.common.exportFailed')); }
  };

  const sortedAssets = [...locationScoped].sort(comparator((a, key) => {
    switch (key) {
      case 'inventory_number': return a.inventory_number || '';
      case 'name': return (a.name || '').toLowerCase();
      case 'asset_type': return a.asset_type || '';
      case 'category_name': return (a.category_name || '').toLowerCase();
      case 'storage_location_name': return (a.storage_location_name || '').toLowerCase();
      case 'condition': return a.condition || '';
      case 'ownership': return a.ownership || 'own';
      case 'supplier_name': return (a.supplier_name || '').toLowerCase();
      case 'purchase_value': return a.purchase_value ?? 0;
      default: return '';
    }
  }));

  const exportThisView = () => exportView({
    filename: 'assets', sheetName: 'Assets', rows: sortedAssets,
    columns: [
      { header: t('auto.lbl.invNo'), key: 'inventory_number' },
      { header: t('auto.lbl.name'), key: 'name' },
      { header: t('auto.lbl.type'), key: 'asset_type' },
      { header: t('auto.lbl.category'), key: 'category_name' },
      { header: t('auto.lbl.storageLocation'), key: 'storage_location_name' },
      { header: t('auto.lbl.delivery'), key: 'delivery_reference' },
      { header: t('auto.lbl.condition'), key: 'condition' },
      { header: t('auto.lbl.ownership'), key: 'ownership' },
      { header: t('auto.lbl.owner'), key: 'owner_customer_name' },
      { header: t('auto.lbl.providedFor'), key: 'provided_for_project_name' },
      { header: t('auto.lbl.returnDue'), key: 'expected_return_date' },
      { header: t('auto.lbl.supplier'), key: 'supplier_name' },
      { header: t('auto.lbl.value'), key: 'purchase_value' },
      { header: t('auto.lbl.currency'), key: 'purchase_currency' },
    ],
  });

  const exportEveryField = async () => {
    try {
      const r = await axios.get(`${API}/inventory/assets`);
      exportFullDump({ filename: 'assets', sheetName: 'Assets', rows: r.data });
    } catch { toast.error(t('inv.common.exportFailed')); }
  };

  const conditionBadge = (cond) => {
    const variant = cond === 'New' ? 'default' : cond === 'Good' ? 'secondary' : cond === 'Scrap' ? 'destructive' : 'outline';
    return <Badge variant={variant}>{cond}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="assets-list">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('inv.assets.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="asset-search-input"
                />
              </div>
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[160px]" data-testid="filter-category">
                <SelectValue placeholder={t('inv.common.category')} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px]" data-testid="filter-type">
                <SelectValue placeholder={t('inv.common.type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tangible">{t('inv.assets.type.tangible')}</SelectItem>
                <SelectItem value="intangible">{t('inv.assets.type.intangible')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterCondition} onValueChange={setFilterCondition}>
              <SelectTrigger className="w-[150px]" data-testid="filter-condition">
                <SelectValue placeholder={t('inv.common.condition')} />
              </SelectTrigger>
              <SelectContent>
                {conditions.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filterLocation} onValueChange={setFilterLocation}>
              <SelectTrigger className="w-[170px]" data-testid="filter-location">
                <SelectValue placeholder={t('inv.form.storageLocation')} />
              </SelectTrigger>
              <SelectContent>
                {storageLocations.map((l) => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                <SelectItem value="unassigned">{t('inv.assets.noLocation')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterOwnership} onValueChange={setFilterOwnership}>
              <SelectTrigger className="w-[170px]" data-testid="filter-ownership">
                <SelectValue placeholder={t('inv.detail.ownership')} />
              </SelectTrigger>
              <SelectContent>
                {ownershipOptions(t).map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-1" /> {t('inv.assets.resetView')}
              </Button>
            )}
            {isAdmin && (
              <Button onClick={() => { setScanPrefill(null); setCreateOpen(true); }} data-testid="create-asset-btn">
                <Plus className="w-4 h-4 mr-1" /> {t('inv.assets.newAsset')}
              </Button>
            )}
            {filterLocation && <DocLangToggle testId="doc-lang-assetslist-0" />}
            {filterLocation && (
              <Button variant="outline" onClick={printStocktake} data-testid="print-stocktake-btn">
                <ClipboardCheck className="w-4 h-4 mr-1" /> {t('inv.assets.printStocktake')}
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" onClick={() => setTransferOpen(true)} data-testid="transfer-location-btn">
                <Truck className="w-4 h-4 mr-1" /> {t('inv.transfer.button')}
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" onClick={() => setPlateScanOpen(true)} data-testid="scan-nameplate-asset-btn">
                <Camera className="w-4 h-4 mr-1" /> {t('inv.nameplate.scanToRegister')}
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-assets-btn">
                <Upload className="w-4 h-4 mr-1" /> {t('inv.import.button')}
              </Button>
            )}
            <Button variant="outline" onClick={() => setScanOpen(true)} data-testid="scan-barcode-btn">
              <ScanLine className="w-4 h-4 mr-1" /> {t('inv.assets.scan')}
            </Button>
            {isAdmin && (
              <>
                <DocLangToggle testId="doc-lang-assetslist-1" />
                <Button variant="outline" size="sm" onClick={() => handleExport('excel')} data-testid="export-excel-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-1" /> {t('auto.inv.excel')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
                  <FileText className="w-4 h-4 mr-1" /> {t('auto.inv.pdf')}
                </Button>
                <Button variant="outline" size="sm" onClick={handleAssetReport} data-testid="asset-report-btn">
                  <Images className="w-4 h-4 mr-1" /> {t('inv.assets.reportPhotos')}
                </Button>
                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={handleComplianceReport} data-testid="compliance-report-btn">
                    <ShieldCheck className="w-4 h-4 mr-1" /> {t('inv.assets.compliance')}
                  </Button>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Batch Action Bar */}
      {selected.size > 0 && isAdmin && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-3 pb-3 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium">{t('inv.assets.selectedCount', { count: selected.size })}</span>
            <Button variant="outline" size="sm" onClick={() => setBatchAction('update')} data-testid="batch-update-btn">
              {t('inv.assets.batchUpdate')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setBulkAssignOpen(true)} data-testid="batch-assign-btn">
              <UserCheck className="w-4 h-4 mr-1" /> {t('inv.assets.assign')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleAssetReport} data-testid="batch-report-btn">
              <Images className="w-4 h-4 mr-1" /> {t('inv.assets.reportPhotos')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setBulkQrOpen(true)} data-testid="batch-labels-btn">
              <QrCode className="w-4 h-4 mr-1" /> {t('inv.assets.printLabels')}
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setBatchAction('delete')} data-testid="batch-delete-btn">
              <Trash2 className="w-4 h-4 mr-1" /> {t('inv.assets.batchDelete')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="clear-selection-btn">
              {t('common.clear')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Assets Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('inv.assets.titleCount', { count: assets.length })}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
          ) : assets.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="no-assets-message">
              No assets found. {isAdmin && 'Create your first asset to get started.'}
            </div>
          ) : (
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    {isAdmin && (
                      <TableHead className="hidden sm:table-cell w-[40px]">
                        <button onClick={toggleAll} className="flex items-center" data-testid="select-all-checkbox">
                          {selected.size === assets.length && assets.length > 0 ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-muted-foreground" />}
                        </button>
                      </TableHead>
                    )}
                    <TableHead className="w-[48px]" />
                    <SortableHeader column="inventory_number" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="hidden sm:table-cell w-[120px] min-w-[112px]">{t('inv.assets.invNo')}</SortableHeader>
                    <SortableHeader column="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('common.name')}</SortableHeader>
                    <TableHead className="hidden xl:table-cell min-w-[150px]">{t('inv.overview.currentlyWith')}</TableHead>
                    <SortableHeader column="asset_type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="hidden md:table-cell">{t('inv.common.type')}</SortableHeader>
                    <SortableHeader column="category_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="hidden lg:table-cell">{t('inv.common.category')}</SortableHeader>
                    <SortableHeader column="storage_location_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="hidden xl:table-cell">{t('inv.form.storageLocation')}</SortableHeader>
                    <SortableHeader column="condition" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="hidden sm:table-cell">{t('inv.common.condition')}</SortableHeader>
                    <SortableHeader column="ownership" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="hidden lg:table-cell">{t('inv.detail.heldAs')}</SortableHeader>
                    <SortableHeader column="supplier_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="hidden xl:table-cell">{t('inv.assets.supplier')}</SortableHeader>
                    <SortableHeader column="purchase_value" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" className="hidden lg:table-cell">{t('inv.common.value')}</SortableHeader>
                    <TableHead className="text-right w-[116px] sm:w-[156px] lg:w-[196px]">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAssets.map((asset) => (
                    <TableRow key={asset.id} className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setDetailAsset(asset)}
                      data-testid={`asset-row-${asset.inventory_number}`}>
                      {isAdmin && (
                        <TableCell className="hidden sm:table-cell align-top">
                          <button role="checkbox" aria-checked={selected.has(asset.id)} aria-label={t('inv.assets.selectAria', { code: asset.inventory_number })}
                            onClick={(e) => { e.stopPropagation(); toggleSelect(asset.id); }} data-testid={`select-asset-${asset.inventory_number}`}>
                            {selected.has(asset.id) ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4 text-muted-foreground" />}
                          </button>
                        </TableCell>
                      )}
                      <TableCell className="align-top p-1 sm:p-2">
                        {/* Spot the tool by sight — far quicker than reading
                            an inventory number off a row. */}
                        <LazyAuthImage
                          src={(asset.pictures || []).length
                            ? `${API}/inventory/assets/${asset.id}/files/${asset.pictures[0]}`
                            : null}
                          alt={asset.name}
                          className="block h-10 w-10 overflow-hidden rounded border bg-muted"
                          testId={`asset-thumb-${asset.inventory_number}`}
                          fallback={(
                            <span className="flex h-10 w-10 items-center justify-center rounded border bg-muted/40 text-muted-foreground"
                              data-testid={`asset-thumb-empty-${asset.inventory_number}`}>
                              <Package className="h-4 w-4" />
                            </span>
                          )}
                        />
                      </TableCell>
                      <TableCell className="hidden sm:table-cell font-mono text-sm font-medium align-top whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <span>{asset.inventory_number}</span>
                          <CopyIconButton label={t('inv.assets.inventoryNumber')} value={asset.inventory_number}
                            testId={`copy-asset-number-${asset.inventory_number}`} />
                        </span>
                      </TableCell>
                      <TableCell className="align-top break-words">
                        <span className="sm:hidden mb-0.5 flex items-center gap-1 font-mono text-xs text-muted-foreground">
                          {asset.inventory_number}
                          <CopyIconButton label={t('inv.assets.inventoryNumber')} value={asset.inventory_number}
                            testId={`copy-asset-number-sm-${asset.inventory_number}`} />
                        </span>
                        <span className="inline-flex items-start gap-1 flex-wrap">
                          <span>{asset.name}</span>
                          <CopyIconButton label={t('inv.assets.assetName')} value={asset.name}
                            testId={`copy-asset-name-${asset.inventory_number}`} />
                        </span>
                        {/* Columns hidden on small screens fold in under the name. */}
                        <span className="md:hidden block text-xs text-muted-foreground">
                          {[asset.asset_type ? t(`inv.assets.type.${asset.asset_type}`, { defaultValue: asset.asset_type }) : null, asset.category_name].filter(Boolean).join(' · ')}
                        </span>
                        <span className="sm:hidden block text-xs text-muted-foreground">{asset.condition || ''}</span>
                        <span className="lg:hidden block text-xs text-muted-foreground">
                          {[asset.ownership && asset.ownership !== 'own' ? ownershipShort(asset.ownership, t) : null,
                            asset.owner_customer_name].filter(Boolean).join(' · ')}
                        </span>
                        <span className="lg:hidden block text-xs text-muted-foreground">
                          {asset.purchase_value ? `${asset.purchase_value.toLocaleString()} ${asset.purchase_currency || ''}` : ''}
                        </span>
                        {/* On a phone the holder folds in here rather than
                            disappearing with its column. */}
                        <div className="xl:hidden mt-1">
                          <AssignmentCell asset={asset} compact />
                        </div>
                      </TableCell>
                      <TableCell className="hidden xl:table-cell align-top">
                        <AssignmentCell asset={asset} />
                      </TableCell>
                      <TableCell className="hidden md:table-cell align-top">
                        <Badge variant="outline">{t(`inv.assets.type.${asset.asset_type}`, { defaultValue: asset.asset_type })}</Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell align-top break-words">{asset.category_name || '-'}</TableCell>
                      <TableCell className="hidden xl:table-cell align-top break-words text-xs" data-testid={`asset-location-${asset.inventory_number}`}>{asset.storage_location_name || '-'}</TableCell>
                      <TableCell className="hidden sm:table-cell align-top">{conditionBadge(asset.condition)}</TableCell>
                      <TableCell className="hidden lg:table-cell align-top">
                        <Badge variant="outline" className={ownershipTone(asset.ownership)}>
                          {ownershipShort(asset.ownership, t)}
                        </Badge>
                        {asset.owner_customer_name && (
                          <span className="block text-xs text-muted-foreground break-words">{asset.owner_customer_name}</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell align-top break-words">{asset.supplier_name || '-'}</TableCell>
                      <TableCell className="text-right hidden lg:table-cell align-top whitespace-nowrap">
                        {asset.purchase_value ? `${asset.purchase_value.toLocaleString()} ${asset.purchase_currency}` : '-'}
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="flex gap-1 flex-wrap justify-end w-[112px] sm:w-[152px] lg:w-[188px] ml-auto">
                          <CopyAllButton testId={`copy-all-asset-${asset.inventory_number}`} title={asset.name}
                            lines={[
                              [t('inv.assets.inventoryNumber'), asset.inventory_number],
                              [t('common.name'), asset.name],
                              [t('inv.common.type'), asset.asset_type],
                              [t('inv.common.category'), asset.category_name],
                              [t('inv.form.storageLocation'), asset.storage_location_name],
                              [t('inv.form.delivery'), asset.delivery_reference],
                              [t('inv.assets.manufacturer'), asset.manufacturer],
                              [t('inv.assets.serialNumber'), asset.serial_number],
                              [t('inv.common.condition'), asset.condition],
                              [t('inv.assets.supplier'), asset.supplier_name],
                            ]} />
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDetailAsset(asset)} data-testid={`view-asset-${asset.inventory_number}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title={t('inv.assets.qrLabel')}
                            onClick={() => setQrAsset(asset)} data-testid={`qr-asset-${asset.inventory_number}`}>
                            <QrCode className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => setDeleteId(asset.id)} data-testid={`delete-asset-${asset.inventory_number}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {bookingDelivery && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
          data-testid="booking-into-delivery-banner">
          <Truck className="h-4 w-4 shrink-0" />
          <span>{t('inv.deliveries.bookingInto', {
            ref: [bookingDelivery.reference, bookingDelivery.supplier_name].filter(Boolean).join(' · '),
          })}</span>
          <Button size="sm" variant="outline" className="ml-auto h-7"
            onClick={() => setPlateScanOpen(true)} data-testid="booking-scan-again-btn">
            <Camera className="h-3.5 w-3.5 mr-1" /> {t('inv.deliveries.scanNext')}
          </Button>
          <button type="button" onClick={() => onBookingDeliveryDone?.()}
            className="font-medium underline underline-offset-2 hover:no-underline"
            data-testid="booking-finish-btn">
            {t('inv.deliveries.finishBooking')}
          </button>
        </div>
      )}

      <FloatingActions actions={[
        { icon: Plus, label: t('inv.assets.newAsset'), tone: 'primary', testId: 'assets-floating-add-btn',
          hidden: !isAdmin, onClick: () => { setScanPrefill(null); setCreateOpen(true); } },
        { icon: Camera, label: t('inv.nameplate.scanToRegister'), testId: 'assets-floating-nameplate-btn',
          hidden: !isAdmin, onClick: () => setPlateScanOpen(true) },
        { icon: ScanLine, label: t('inv.assets.scanBarcode'), testId: 'assets-floating-scan-btn',
          onClick: () => setScanOpen(true) },
        { icon: FileSpreadsheet, label: t('inv.assets.exportView'),
          testId: 'assets-floating-export-btn', onClick: exportThisView },
        { icon: Database, label: t('inv.assets.exportAll'),
          testId: 'assets-floating-export-all-btn', hidden: !isAdmin, onClick: exportEveryField },
      ]} />

      {/* Create Dialog */}
      {createOpen && (
        <AssetForm
          open={createOpen}
          onClose={() => { setCreateOpen(false); setScanPrefill(null); }}
          onSaved={handleSaved}
          categories={categories}
          conditions={conditions}
          suppliers={suppliers}
          config={config}
          prefill={scanPrefill}
        />
      )}

      {/* Register a unit by scanning its rating plate: the catalogue entry is
          resolved first, then the asset form opens pre-filled for review. */}
      <NameplateScanDialog
        open={plateScanOpen}
        onOpenChange={setPlateScanOpen}
        categories={categories}
        mode="asset"
        onSaved={() => config.onModelsChange?.()}
        onReadyForAsset={({ model, plate }) => {
          setScanPrefill({ model, plate, delivery_id: bookingDelivery?.id || null });
          setCreateOpen(true);
        }}
      />

      {/* Edit Dialog */}
      {editAsset && (
        <AssetForm
          open={!!editAsset}
          onClose={() => setEditAsset(null)}
          onSaved={handleSaved}
          asset={editAsset}
          categories={categories}
          conditions={conditions}
          suppliers={suppliers}
          config={config}
        />
      )}

      {/* Detail Sheet */}
      {detailAsset && (
        <AssetDetail
          asset={detailAsset}
          open={!!detailAsset}
          onClose={() => setDetailAsset(null)}
          onEdit={(a) => { setDetailAsset(null); setEditAsset(a); }}
          onDeleted={() => { setDetailAsset(null); fetchAssets(); onDataChange?.(); }}
          onUpdated={(updated) => { setDetailAsset(updated); fetchAssets(); }}
          categories={categories}
          conditions={conditions}
          suppliers={suppliers}
          employees={employees}
          projects={projects}
          isAdmin={isAdmin}
          config={config}
          navigateTo={navigateTo}
        />
      )}

      {/* QR labels — one asset, or every selected asset */}
      {qrAsset && (
        <QrLabelDialog open={!!qrAsset} onOpenChange={(o) => { if (!o) setQrAsset(null); }}
          kind="asset" id={qrAsset.id}
          title={`${qrAsset.inventory_number} — ${qrAsset.name}`} isAdmin={isAdmin} />
      )}
      {bulkQrOpen && (
        <QrLabelDialog open={bulkQrOpen} onOpenChange={setBulkQrOpen}
          kind="asset" ids={Array.from(selected)} isAdmin={isAdmin} />
      )}


      {bulkAssignOpen && (
        <MatrixAssignDialog
          open={bulkAssignOpen}
          onOpenChange={setBulkAssignOpen}
          assetIds={Array.from(selected)}
          projects={projects}
          employees={employees}
          onDone={() => { setSelected(new Set()); fetchAssets(); onDataChange?.(); }}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('inv.assets.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('inv.assets.deleteHint')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Barcode Scanner */}
      <BarcodeScanner
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onAssetFound={(a) => { setScanOpen(false); setDetailAsset(a); }}
        onNotFound={(code) => { setScanOpen(false); setCreateOpen(true); }}
        categories={categories}
        onModelSaved={() => { config.onModelsChange?.(); setCreateOpen(true); }}
      />

      {/* Batch Delete Dialog */}
      <AlertDialog open={batchAction === 'delete'} onOpenChange={() => setBatchAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('inv.assets.batchDeleteTitle', { count: selected.size })}</AlertDialogTitle>
            <AlertDialogDescription>{t('inv.assets.batchDeleteHint')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleBatchDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-batch-delete-btn">{t('inv.assets.deleteAll')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Batch Update Dialog */}
      {batchAction === 'update' && (
        <AlertDialog open onOpenChange={() => setBatchAction(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('inv.assets.batchUpdateTitle', { count: selected.size })}</AlertDialogTitle>
              <AlertDialogDescription>{t('inv.assets.batchUpdateHint')}</AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-xs text-muted-foreground">{t('inv.common.condition')}</label>
                <Select value={batchCondition} onValueChange={setBatchCondition}>
                  <SelectTrigger data-testid="batch-condition-select"><SelectValue placeholder={t('inv.assets.noChange')} /></SelectTrigger>
                  <SelectContent>
                    {conditions.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">{t('inv.common.category')}</label>
                <Select value={batchCategory} onValueChange={setBatchCategory}>
                  <SelectTrigger data-testid="batch-category-select"><SelectValue placeholder={t('inv.assets.noChange')} /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleBatchUpdate} disabled={!batchCondition && !batchCategory} data-testid="confirm-batch-update-btn">{t('inv.assets.updateAll')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {transferOpen && (
        <LocationTransferDialog
          open={transferOpen}
          onOpenChange={setTransferOpen}
          locations={storageLocations}
          sourceId={filterLocation}
          onDone={() => { fetchAssets(); onDataChange?.(); }}
        />
      )}

      <BulkImportDialog open={importOpen} onOpenChange={setImportOpen} kind="assets"
        onImported={() => { fetchAssets(); onDataChange?.(); }} />
    </div>
  );
};

export default AssetsList;
